import React, { useState, useEffect, useCallback } from 'react';
import {
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Button,
  IconButton,
  Tooltip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  TextField,
  Grid,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  AddCircleOutline as AddCircleOutlineIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FileDownload as FileDownloadIcon,
  FileUpload as FileUploadIcon,
  Save as SaveIcon,
  FolderOpen as FolderOpenIcon,
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { apiClient } from '@/services/api';
import { showSuccessMessage, showErrorMessage } from '@/components/common/GlobalSnackbar';
import DepartmentSelfEvaluationIndicatorImportDialog from './DepartmentSelfEvaluationIndicatorImportDialog';
import UploadedDocumentsDialog from './UploadedDocumentsDialog';

// ==================== 类型定义 ====================

// 部门自评指标
interface DepartmentSelfEvaluationIndicator {
  id: number;
  project_id: number;
  level1_code: string;
  level1_name: string;
  level2_code: string;
  level2_name: string;
  level3_code: string;
  level3_name: string;
  indicator_value: string;
  value_description: string;
  execution_situation: string;
  expected_completion: string;
  deviation_reason: string;
  reason_description: string;
  completion_probability: string;
  note: string;
  ai_recognition_basis: string;
  score_value: string;
  score: string;
  deviation_degree: string;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

// 年度任务
interface AnnualTask {
  id: number;
  project_id: number;
  task_name: string;
  main_content: string;
  actual_completion: string;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

// 资金执行情况
interface FundExecution {
  id: number;
  project_id: number;
  fund_type: string;
  initial_budget: string;
  full_year_budget: string;
  full_year_execution: string;
  score_value: string;
  score: string;
  sort_order: number;
}

// 项目元信息
interface ProjectMeta {
  unit_name: string;
  annual_target: string;
  actual_completion: string;
}

// ==================== 主组件 ====================

const DepartmentSelfEvaluationIndicatorManager: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();

  // 项目信息
  const [project, setProject] = useState<any>(null);
  const [meta, setMeta] = useState<ProjectMeta>({
    unit_name: '',
    annual_target: '',
    actual_completion: '',
  });
  const [savingMeta, setSavingMeta] = useState(false);

  // 指标
  const [indicators, setIndicators] = useState<DepartmentSelfEvaluationIndicator[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 年度任务
  const [annualTasks, setAnnualTasks] = useState<AnnualTask[]>([]);

  // 资金执行
  const [fundExecutions, setFundExecutions] = useState<FundExecution[]>([]);

  // 对话框状态
  const [addIndicatorDialogOpen, setAddIndicatorDialogOpen] = useState(false);
  const [editIndicatorDialogOpen, setEditIndicatorDialogOpen] = useState(false);
  const [deleteIndicatorDialogOpen, setDeleteIndicatorDialogOpen] = useState(false);
  const [editingIndicator, setEditingIndicator] = useState<DepartmentSelfEvaluationIndicator | null>(null);
  const [deletingIndicator, setDeletingIndicator] = useState<DepartmentSelfEvaluationIndicator | null>(null);

  const [addTaskDialogOpen, setAddTaskDialogOpen] = useState(false);
  const [editTaskDialogOpen, setEditTaskDialogOpen] = useState(false);
  const [deleteTaskDialogOpen, setDeleteTaskDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<AnnualTask | null>(null);
  const [deletingTask, setDeletingTask] = useState<AnnualTask | null>(null);

  // 批量删除
  const [selectedIndicatorIds, setSelectedIndicatorIds] = useState<number[]>([]);
  const [batchDeleteDialogOpen, setBatchDeleteDialogOpen] = useState(false);
  const [isBatchDeleting, setIsBatchDeleting] = useState(false);

  // 导出
  const [isExporting, setIsExporting] = useState(false);
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);

  // 导入
  const [importDialogOpen, setImportDialogOpen] = useState(false);

  // 文档管理弹窗状态
  const [documentsDialogOpen, setDocumentsDialogOpen] = useState(false);

  // 表单数据
  const [indicatorForm, setIndicatorForm] = useState({
    level1_name: '',
    level2_name: '',
    level3_name: '',
    indicator_value: '',
    value_description: '',
    execution_situation: '',
    deviation_reason: '',
    note: '',
    score_value: '',
    score: '',
    deviation_degree: '',
  });

  const [taskForm, setTaskForm] = useState({
    task_name: '',
    main_content: '',
    actual_completion: '',
  });

  // ==================== 数据获取 ====================

  // 获取项目信息
  const fetchProject = useCallback(async () => {
    if (!projectId) return;
    try {
      const response = await apiClient.get(`/projects/${projectId}`);
      if (response.success && response.data) {
        setProject(response.data);
      }
    } catch (err: any) {
      console.error('获取项目信息失败:', err);
    }
  }, [projectId]);

  // 获取项目元信息
  const fetchMeta = useCallback(async () => {
    if (!projectId) return;
    try {
      const response = await apiClient.get(`/projects/${projectId}/performance-meta`);
      if (response.success && response.data) {
        setMeta({
          unit_name: response.data.unit_name || '',
          annual_target: response.data.annual_target || '',
          actual_completion: response.data.actual_completion || '',
        });
      }
    } catch (err: any) {
      console.error('获取项目元信息失败:', err);
    }
  }, [projectId]);

  // 获取指标列表
  const fetchIndicators = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get(`/projects/${projectId}/department-self-evaluation-indicators?page=1&page_size=1000`);
      if (response.success && response.data) {
        // 确保是数组
        const data = Array.isArray(response.data) ? response.data : [];
        setIndicators(data);
      } else {
        setError(response.message || '获取指标列表失败');
        setIndicators([]);
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || '获取指标列表失败');
      setIndicators([]);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  // 获取年度任务
  const fetchAnnualTasks = useCallback(async () => {
    if (!projectId) return;
    try {
      const response = await apiClient.get(`/projects/${projectId}/department-annual-tasks`);
      if (response.success && response.data) {
        // 确保是数组
        const data = Array.isArray(response.data) ? response.data : [];
        setAnnualTasks(data);
      }
    } catch (err: any) {
      console.error('获取年度任务失败:', err);
      setAnnualTasks([]);
    }
  }, [projectId]);

  // 获取资金执行情况
  const fetchFundExecutions = useCallback(async () => {
    if (!projectId) return;
    try {
      const response = await apiClient.get(`/projects/${projectId}/fund-executions`);
      if (response.success && response.data) {
        // 后端返回 { items: [...], total: ... }
        const data = response.data.items || response.data;
        setFundExecutions(Array.isArray(data) ? data : []);
      }
    } catch (err: any) {
      console.error('获取资金执行情况失败:', err);
      setFundExecutions([]);
    }
  }, [projectId]);

  useEffect(() => {
    fetchProject();
    fetchMeta();
    fetchIndicators();
    fetchAnnualTasks();
    fetchFundExecutions();
  }, [fetchProject, fetchMeta, fetchIndicators, fetchAnnualTasks, fetchFundExecutions]);

  // ==================== 项目元信息操作 ====================

  const handleSaveMeta = async () => {
    if (!projectId) return;
    setSavingMeta(true);
    try {
      const response = await apiClient.put(`/projects/${projectId}/performance-meta`, meta);
      if (response.success) {
        showSuccessMessage('项目信息保存成功');
      } else {
        showErrorMessage(response.message || '保存失败');
      }
    } catch (err: any) {
      showErrorMessage(err.response?.data?.detail || '保存失败');
    } finally {
      setSavingMeta(false);
    }
  };

  // ==================== 指标操作 ====================

  const handleAddIndicator = async () => {
    if (!projectId) return;
    if (!indicatorForm.level3_name || !indicatorForm.indicator_value) {
      showErrorMessage('三级指标名称和年度指标值是必填项');
      return;
    }
    try {
      const response = await apiClient.post(
        `/projects/${projectId}/department-self-evaluation-indicators`,
        indicatorForm
      );
      if (response.success) {
        showSuccessMessage('指标添加成功');
        setAddIndicatorDialogOpen(false);
        resetIndicatorForm();
        fetchIndicators();
      } else {
        showErrorMessage(response.message || '添加指标失败');
      }
    } catch (err: any) {
      showErrorMessage(err.response?.data?.detail || '添加指标失败');
    }
  };

  const handleUpdateIndicator = async () => {
    if (!projectId || !editingIndicator) return;
    try {
      const response = await apiClient.put(
        `/projects/${projectId}/department-self-evaluation-indicators/${editingIndicator.id}`,
        indicatorForm
      );
      if (response.success) {
        showSuccessMessage('指标更新成功');
        setEditIndicatorDialogOpen(false);
        setEditingIndicator(null);
        resetIndicatorForm();
        fetchIndicators();
      } else {
        showErrorMessage(response.message || '更新指标失败');
      }
    } catch (err: any) {
      showErrorMessage(err.response?.data?.detail || '更新指标失败');
    }
  };

  const handleDeleteIndicator = async () => {
    if (!projectId || !deletingIndicator) return;
    try {
      const response = await apiClient.delete(
        `/projects/${projectId}/department-self-evaluation-indicators/${deletingIndicator.id}`
      );
      if (response.success) {
        showSuccessMessage('指标删除成功');
        setDeleteIndicatorDialogOpen(false);
        setDeletingIndicator(null);
        fetchIndicators();
      } else {
        showErrorMessage(response.message || '删除指标失败');
      }
    } catch (err: any) {
      showErrorMessage(err.response?.data?.detail || '删除指标失败');
    }
  };

  const handleBatchDeleteIndicators = async () => {
    if (!projectId || selectedIndicatorIds.length === 0) return;
    setIsBatchDeleting(true);
    try {
      const response = await apiClient.post(
        `/projects/${projectId}/department-self-evaluation-indicators/batch-delete`,
        { indicator_ids: selectedIndicatorIds }
      );
      if (response.success) {
        showSuccessMessage(`成功删除 ${selectedIndicatorIds.length} 个指标`);
        setSelectedIndicatorIds([]);
        setBatchDeleteDialogOpen(false);
        fetchIndicators();
      } else {
        showErrorMessage(response.message || '批量删除失败');
      }
    } catch (err: any) {
      showErrorMessage(err.response?.data?.detail || '批量删除失败');
    } finally {
      setIsBatchDeleting(false);
    }
  };

  const resetIndicatorForm = () => {
    setIndicatorForm({
      level1_name: '',
      level2_name: '',
      level3_name: '',
      indicator_value: '',
      value_description: '',
      execution_situation: '',
      deviation_reason: '',
      note: '',
      score_value: '',
      score: '',
      deviation_degree: '',
    });
  };

  const openEditIndicatorDialog = (indicator: DepartmentSelfEvaluationIndicator) => {
    setEditingIndicator(indicator);
    setIndicatorForm({
      level1_name: indicator.level1_name,
      level2_name: indicator.level2_name,
      level3_name: indicator.level3_name,
      indicator_value: indicator.indicator_value,
      value_description: indicator.value_description || '',
      execution_situation: indicator.execution_situation || '',
      deviation_reason: indicator.deviation_reason || '',
      note: indicator.note || '',
      score_value: indicator.score_value || '',
      score: indicator.score || '',
      deviation_degree: indicator.deviation_degree || '',
    });
    setEditIndicatorDialogOpen(true);
  };

  // ==================== 年度任务操作 ====================

  const handleAddTask = async () => {
    if (!projectId) return;
    if (!taskForm.task_name) {
      showErrorMessage('任务名称是必填项');
      return;
    }
    try {
      const response = await apiClient.post(
        `/projects/${projectId}/department-annual-tasks`,
        { ...taskForm, sort_order: annualTasks.length }
      );
      if (response.success) {
        showSuccessMessage('任务添加成功');
        setAddTaskDialogOpen(false);
        resetTaskForm();
        fetchAnnualTasks();
      } else {
        showErrorMessage(response.message || '添加任务失败');
      }
    } catch (err: any) {
      showErrorMessage(err.response?.data?.detail || '添加任务失败');
    }
  };

  const handleUpdateTask = async () => {
    if (!projectId || !editingTask) return;
    try {
      const response = await apiClient.put(
        `/projects/${projectId}/department-annual-tasks/${editingTask.id}`,
        taskForm
      );
      if (response.success) {
        showSuccessMessage('任务更新成功');
        setEditTaskDialogOpen(false);
        setEditingTask(null);
        resetTaskForm();
        fetchAnnualTasks();
      } else {
        showErrorMessage(response.message || '更新任务失败');
      }
    } catch (err: any) {
      showErrorMessage(err.response?.data?.detail || '更新任务失败');
    }
  };

  const handleDeleteTask = async () => {
    if (!projectId || !deletingTask) return;
    try {
      const response = await apiClient.delete(
        `/projects/${projectId}/department-annual-tasks/${deletingTask.id}`
      );
      if (response.success) {
        showSuccessMessage('任务删除成功');
        setDeleteTaskDialogOpen(false);
        setDeletingTask(null);
        fetchAnnualTasks();
      } else {
        showErrorMessage(response.message || '删除任务失败');
      }
    } catch (err: any) {
      showErrorMessage(err.response?.data?.detail || '删除任务失败');
    }
  };

  const resetTaskForm = () => {
    setTaskForm({
      task_name: '',
      main_content: '',
      actual_completion: '',
    });
  };

  const openEditTaskDialog = (task: AnnualTask) => {
    setEditingTask(task);
    setTaskForm({
      task_name: task.task_name,
      main_content: task.main_content,
      actual_completion: task.actual_completion || '',
    });
    setEditTaskDialogOpen(true);
  };

  // ==================== 资金执行情况操作 ====================

  const handleSaveFundExecutions = async () => {
    if (!projectId) return;
    try {
      const response = await apiClient.post(
        `/projects/${projectId}/fund-executions/batch-save`,
        { executions: fundExecutions }
      );
      if (response.success) {
        showSuccessMessage('资金执行情况保存成功');
        fetchFundExecutions();
      } else {
        showErrorMessage(response.message || '保存失败');
      }
    } catch (err: any) {
      showErrorMessage(err.response?.data?.detail || '保存失败');
    }
  };

  // ==================== 导出操作 ====================

  const handleExportExcel = async () => {
    if (!projectId) return;
    setExportMenuAnchor(null);
    setIsExporting(true);
    try {
      const response = await fetch(
        `/api/v1/projects/${projectId}/department-self-evaluation-indicators/export-excel`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token') || ''}`,
          },
        }
      );
      if (!response.ok) throw new Error('导出失败');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `部门自评表_${project?.project_name || projectId}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      showSuccessMessage('导出成功');
    } catch (err: any) {
      showErrorMessage(err.message || '导出失败');
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportWord = async () => {
    if (!projectId) return;
    setExportMenuAnchor(null);
    setIsExporting(true);
    try {
      const response = await fetch(
        `/api/v1/projects/${projectId}/department-self-evaluation-indicators/export-word`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token') || ''}`,
          },
        }
      );
      if (!response.ok) throw new Error('导出失败');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `部门自评表_${project?.project_name || projectId}.docx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      showSuccessMessage('导出成功');
    } catch (err: any) {
      showErrorMessage(err.message || '导出失败');
    } finally {
      setIsExporting(false);
    }
  };

  // ==================== 导入操作 ====================

  const handleImportSuccess = () => {
    // 刷新所有数据
    fetchIndicators();
    fetchAnnualTasks();
    fetchFundExecutions();
    fetchMeta();
  };

  // ==================== 选择操作 ====================

  const handleSelectIndicator = (id: number) => {
    setSelectedIndicatorIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleSelectAllIndicators = () => {
    if (selectedIndicatorIds.length === indicators.length) {
      setSelectedIndicatorIds([]);
    } else {
      setSelectedIndicatorIds(indicators.map(i => i.id));
    }
  };

  // ==================== 渲染 ====================

  if (loading && indicators.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* 标题和操作栏 */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h1">
          部门自评指标管理
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<AddCircleOutlineIcon />}
            onClick={() => { resetIndicatorForm(); setAddIndicatorDialogOpen(true); }}
          >
            添加指标
          </Button>
          <Button
            variant="outlined"
            startIcon={<FolderOpenIcon />}
            onClick={() => setDocumentsDialogOpen(true)}
          >
            文档管理
          </Button>
          <Button
            variant="outlined"
            startIcon={<FileUploadIcon />}
            onClick={() => setImportDialogOpen(true)}
          >
            导入
          </Button>
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={(e) => setExportMenuAnchor(e.currentTarget)}
            disabled={isExporting}
          >
            {isExporting ? '导出中...' : '导出'}
          </Button>
          <Menu
            anchorEl={exportMenuAnchor}
            open={Boolean(exportMenuAnchor)}
            onClose={() => setExportMenuAnchor(null)}
          >
            <MenuItem onClick={handleExportExcel} disabled={isExporting}>
              {isExporting ? '导出中...' : '导出为 Excel (.xlsx)'}
            </MenuItem>
            <MenuItem onClick={handleExportWord} disabled={isExporting}>
              {isExporting ? '导出中...' : '导出为 Word (.docx)'}
            </MenuItem>
          </Menu>
        </Stack>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* 1. 项目信息 */}
      <Paper sx={{ p: 2, mb: 2 }} variant="outlined">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>项目信息</Typography>
          <Button size="small" variant="contained" onClick={handleSaveMeta} disabled={savingMeta} startIcon={<SaveIcon />}>
            {savingMeta ? '保存中...' : '保存项目信息'}
          </Button>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField
              label="年度"
              fullWidth
              size="small"
              value={project?.contract_year || ''}
              InputProps={{ readOnly: true }}
            />
          </Grid>
          <Grid item xs={12} sm={8}>
            <TextField
              label="部门(单位)名称"
              fullWidth
              size="small"
              value={meta.unit_name}
              onChange={(e) => setMeta({ ...meta, unit_name: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="年度履职目标"
              fullWidth
              multiline
              rows={2}
              size="small"
              value={meta.annual_target}
              onChange={(e) => setMeta({ ...meta, annual_target: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="实际完成情况"
              fullWidth
              multiline
              rows={2}
              size="small"
              value={meta.actual_completion}
              onChange={(e) => setMeta({ ...meta, actual_completion: e.target.value })}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* 2. 部门整体支出情况 */}
      <Paper sx={{ p: 2, mb: 2 }} variant="outlined">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>部门整体支出情况</Typography>
          <Button size="small" variant="contained" onClick={handleSaveFundExecutions} startIcon={<SaveIcon />}>
            保存资金情况
          </Button>
        </Box>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead sx={{ backgroundColor: 'action.hover' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', width: 150 }}>资金来源类型</TableCell>
                <TableCell sx={{ fontWeight: 'bold', minWidth: 100 }}>年初预算数</TableCell>
                <TableCell sx={{ fontWeight: 'bold', minWidth: 100 }}>全年预算数</TableCell>
                <TableCell sx={{ fontWeight: 'bold', minWidth: 100 }}>全年执行数</TableCell>
                <TableCell sx={{ fontWeight: 'bold', minWidth: 80 }}>执行率</TableCell>
                <TableCell sx={{ fontWeight: 'bold', minWidth: 60 }}>分值</TableCell>
                <TableCell sx={{ fontWeight: 'bold', minWidth: 60 }}>得分</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {fundExecutions.map((exec, index) => {
                const budget = parseFloat(exec.full_year_budget || '0');
                const execution = parseFloat(exec.full_year_execution || '0');
                const executionRate = budget > 0 ? ((execution / budget) * 100).toFixed(2) + '%' : '-';
                return (
                  <TableRow key={exec.id || index}>
                    <TableCell>{exec.fund_type}</TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        type="number"
                        fullWidth
                        value={exec.initial_budget || ''}
                        onChange={(e) => {
                          const updated = [...fundExecutions];
                          updated[index] = { ...updated[index], initial_budget: e.target.value };
                          setFundExecutions(updated);
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        type="number"
                        fullWidth
                        value={exec.full_year_budget || ''}
                        onChange={(e) => {
                          const updated = [...fundExecutions];
                          updated[index] = { ...updated[index], full_year_budget: e.target.value };
                          setFundExecutions(updated);
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        type="number"
                        fullWidth
                        value={exec.full_year_execution || ''}
                        onChange={(e) => {
                          const updated = [...fundExecutions];
                          updated[index] = { ...updated[index], full_year_execution: e.target.value };
                          setFundExecutions(updated);
                        }}
                      />
                    </TableCell>
                    <TableCell>{executionRate}</TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        value={exec.score_value || ''}
                        onChange={(e) => {
                          const updated = [...fundExecutions];
                          updated[index] = { ...updated[index], score_value: e.target.value };
                          setFundExecutions(updated);
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        value={exec.score || ''}
                        onChange={(e) => {
                          const updated = [...fundExecutions];
                          updated[index] = { ...updated[index], score: e.target.value };
                          setFundExecutions(updated);
                        }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* 3. 年度主要任务 */}
      <Paper sx={{ p: 2, mb: 2 }} variant="outlined">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
            年度主要任务 ({annualTasks.length})
          </Typography>
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddCircleOutlineIcon />}
            onClick={() => { resetTaskForm(); setAddTaskDialogOpen(true); }}
          >
            添加任务
          </Button>
        </Box>
        {annualTasks.length > 0 ? (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead sx={{ backgroundColor: 'action.hover' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', width: 50 }}>序号</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: 200 }}>任务名称</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>主要内容</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: 250 }}>实际完成情况</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: 100, textAlign: 'center' }}>操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {annualTasks.map((task, index) => (
                  <TableRow key={task.id}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{task.task_name}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {task.main_content}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {task.actual_completion || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="编辑">
                        <IconButton size="small" onClick={() => openEditTaskDialog(task)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="删除">
                        <IconButton size="small" onClick={() => { setDeletingTask(task); setDeleteTaskDialogOpen(true); }} color="error">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50', borderRadius: 1, border: '1px dashed', borderColor: 'grey.300' }}>
            <Typography variant="body2" color="text.secondary">
              暂无年度主要任务，请点击"添加任务"按钮
            </Typography>
          </Box>
        )}
      </Paper>

      {/* 4. 绩效指标列表 */}
      <Paper sx={{ p: 2 }} variant="outlined">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
            绩效指标 ({indicators.length})
          </Typography>
          {selectedIndicatorIds.length > 0 && (
            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={() => setBatchDeleteDialogOpen(true)}
            >
              批量删除 ({selectedIndicatorIds.length})
            </Button>
          )}
        </Box>
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: '60vh', overflowX: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead sx={{ backgroundColor: 'action.hover' }}>
              <TableRow>
                <TableCell padding="checkbox" sx={{ fontWeight: 'bold' }}>
                  <Checkbox
                    checked={indicators.length > 0 && selectedIndicatorIds.length === indicators.length}
                    indeterminate={selectedIndicatorIds.length > 0 && selectedIndicatorIds.length < indicators.length}
                    onChange={handleSelectAllIndicators}
                    size="small"
                  />
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: 50 }}>序号</TableCell>
                <TableCell sx={{ fontWeight: 'bold', minWidth: 100 }}>一级指标</TableCell>
                <TableCell sx={{ fontWeight: 'bold', minWidth: 120 }}>二级指标</TableCell>
                <TableCell sx={{ fontWeight: 'bold', minWidth: 150 }}>三级指标</TableCell>
                <TableCell sx={{ fontWeight: 'bold', minWidth: 100 }}>年度指标值</TableCell>
                <TableCell sx={{ fontWeight: 'bold', minWidth: 100 }}>实际完成值</TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: 60 }}>分值</TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: 60 }}>得分</TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: 70 }}>偏差度</TableCell>
                <TableCell sx={{ fontWeight: 'bold', minWidth: 150 }}>偏差原因</TableCell>
                <TableCell sx={{ fontWeight: 'bold', minWidth: 100 }}>备注</TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: 80, textAlign: 'center' }}>操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {indicators.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={13} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                      暂无指标数据，请添加或导入指标
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                indicators.map((indicator, index) => (
                  <TableRow key={indicator.id} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedIndicatorIds.includes(indicator.id)}
                        onChange={() => handleSelectIndicator(indicator.id)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{indicator.level1_name}</TableCell>
                    <TableCell>{indicator.level2_name}</TableCell>
                    <TableCell>{indicator.level3_name}</TableCell>
                    <TableCell>{indicator.indicator_value}</TableCell>
                    <TableCell>{indicator.execution_situation || '-'}</TableCell>
                    <TableCell>{indicator.score_value || '-'}</TableCell>
                    <TableCell>{indicator.score || '-'}</TableCell>
                    <TableCell>{indicator.deviation_degree || '-'}</TableCell>
                    <TableCell>
                      <Tooltip title={indicator.deviation_reason || ''}>
                        <Typography variant="body2" sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {indicator.deviation_reason || '-'}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={indicator.note || ''}>
                        <Typography variant="body2" sx={{ maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {indicator.note || '-'}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="编辑">
                        <IconButton size="small" onClick={() => openEditIndicatorDialog(indicator)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="删除">
                        <IconButton size="small" onClick={() => { setDeletingIndicator(indicator); setDeleteIndicatorDialogOpen(true); }} color="error">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* ==================== 对话框 ==================== */}

      {/* 添加指标对话框 */}
      <Dialog open={addIndicatorDialogOpen} onClose={() => setAddIndicatorDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>添加绩效指标</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <TextField label="一级指标" fullWidth value={indicatorForm.level1_name}
                onChange={(e) => setIndicatorForm({ ...indicatorForm, level1_name: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField label="二级指标" fullWidth value={indicatorForm.level2_name}
                onChange={(e) => setIndicatorForm({ ...indicatorForm, level2_name: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField label="三级指标名称 *" fullWidth required value={indicatorForm.level3_name}
                onChange={(e) => setIndicatorForm({ ...indicatorForm, level3_name: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField label="年度指标值 *" fullWidth required value={indicatorForm.indicator_value}
                onChange={(e) => setIndicatorForm({ ...indicatorForm, indicator_value: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField label="实际完成值" fullWidth multiline rows={2} value={indicatorForm.execution_situation}
                onChange={(e) => setIndicatorForm({ ...indicatorForm, execution_situation: e.target.value })} />
            </Grid>
            <Grid item xs={4}>
              <TextField label="分值" fullWidth value={indicatorForm.score_value}
                onChange={(e) => setIndicatorForm({ ...indicatorForm, score_value: e.target.value })} />
            </Grid>
            <Grid item xs={4}>
              <TextField label="得分" fullWidth value={indicatorForm.score}
                onChange={(e) => setIndicatorForm({ ...indicatorForm, score: e.target.value })} />
            </Grid>
            <Grid item xs={4}>
              <TextField label="偏差度%" fullWidth value={indicatorForm.deviation_degree}
                onChange={(e) => setIndicatorForm({ ...indicatorForm, deviation_degree: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField label="偏差原因" fullWidth multiline rows={2} value={indicatorForm.deviation_reason}
                onChange={(e) => setIndicatorForm({ ...indicatorForm, deviation_reason: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField label="备注" fullWidth multiline rows={2} value={indicatorForm.note}
                onChange={(e) => setIndicatorForm({ ...indicatorForm, note: e.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddIndicatorDialogOpen(false)}>取消</Button>
          <Button onClick={handleAddIndicator} variant="contained">添加</Button>
        </DialogActions>
      </Dialog>

      {/* 编辑指标对话框 */}
      <Dialog open={editIndicatorDialogOpen} onClose={() => setEditIndicatorDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>编辑绩效指标</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <TextField label="一级指标" fullWidth value={indicatorForm.level1_name}
                onChange={(e) => setIndicatorForm({ ...indicatorForm, level1_name: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField label="二级指标" fullWidth value={indicatorForm.level2_name}
                onChange={(e) => setIndicatorForm({ ...indicatorForm, level2_name: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField label="三级指标名称 *" fullWidth required value={indicatorForm.level3_name}
                onChange={(e) => setIndicatorForm({ ...indicatorForm, level3_name: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField label="年度指标值 *" fullWidth required value={indicatorForm.indicator_value}
                onChange={(e) => setIndicatorForm({ ...indicatorForm, indicator_value: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField label="实际完成值" fullWidth multiline rows={2} value={indicatorForm.execution_situation}
                onChange={(e) => setIndicatorForm({ ...indicatorForm, execution_situation: e.target.value })} />
            </Grid>
            <Grid item xs={4}>
              <TextField label="分值" fullWidth value={indicatorForm.score_value}
                onChange={(e) => setIndicatorForm({ ...indicatorForm, score_value: e.target.value })} />
            </Grid>
            <Grid item xs={4}>
              <TextField label="得分" fullWidth value={indicatorForm.score}
                onChange={(e) => setIndicatorForm({ ...indicatorForm, score: e.target.value })} />
            </Grid>
            <Grid item xs={4}>
              <TextField label="偏差度%" fullWidth value={indicatorForm.deviation_degree}
                onChange={(e) => setIndicatorForm({ ...indicatorForm, deviation_degree: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField label="偏差原因" fullWidth multiline rows={2} value={indicatorForm.deviation_reason}
                onChange={(e) => setIndicatorForm({ ...indicatorForm, deviation_reason: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField label="备注" fullWidth multiline rows={2} value={indicatorForm.note}
                onChange={(e) => setIndicatorForm({ ...indicatorForm, note: e.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditIndicatorDialogOpen(false)}>取消</Button>
          <Button onClick={handleUpdateIndicator} variant="contained">保存</Button>
        </DialogActions>
      </Dialog>

      {/* 删除指标确认对话框 */}
      <Dialog open={deleteIndicatorDialogOpen} onClose={() => setDeleteIndicatorDialogOpen(false)}>
        <DialogTitle>确认删除</DialogTitle>
        <DialogContent>
          <Typography>确定要删除指标 "{deletingIndicator?.level3_name}" 吗？此操作不可撤销。</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteIndicatorDialogOpen(false)}>取消</Button>
          <Button onClick={handleDeleteIndicator} color="error" variant="contained">删除</Button>
        </DialogActions>
      </Dialog>

      {/* 批量删除确认对话框 */}
      <Dialog open={batchDeleteDialogOpen} onClose={() => setBatchDeleteDialogOpen(false)}>
        <DialogTitle>确认批量删除</DialogTitle>
        <DialogContent>
          <Typography>确定要删除选中的 {selectedIndicatorIds.length} 个指标吗？此操作不可撤销。</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBatchDeleteDialogOpen(false)}>取消</Button>
          <Button onClick={handleBatchDeleteIndicators} color="error" variant="contained" disabled={isBatchDeleting}>
            {isBatchDeleting ? '删除中...' : '确认删除'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 添加任务对话框 */}
      <Dialog open={addTaskDialogOpen} onClose={() => setAddTaskDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>添加年度任务</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField label="任务名称 *" fullWidth required value={taskForm.task_name}
                onChange={(e) => setTaskForm({ ...taskForm, task_name: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField label="主要内容" fullWidth multiline rows={3} value={taskForm.main_content}
                onChange={(e) => setTaskForm({ ...taskForm, main_content: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField label="实际完成情况" fullWidth multiline rows={3} value={taskForm.actual_completion}
                onChange={(e) => setTaskForm({ ...taskForm, actual_completion: e.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddTaskDialogOpen(false)}>取消</Button>
          <Button onClick={handleAddTask} variant="contained">添加</Button>
        </DialogActions>
      </Dialog>

      {/* 编辑任务对话框 */}
      <Dialog open={editTaskDialogOpen} onClose={() => setEditTaskDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>编辑年度任务</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField label="任务名称 *" fullWidth required value={taskForm.task_name}
                onChange={(e) => setTaskForm({ ...taskForm, task_name: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField label="主要内容" fullWidth multiline rows={3} value={taskForm.main_content}
                onChange={(e) => setTaskForm({ ...taskForm, main_content: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField label="实际完成情况" fullWidth multiline rows={3} value={taskForm.actual_completion}
                onChange={(e) => setTaskForm({ ...taskForm, actual_completion: e.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditTaskDialogOpen(false)}>取消</Button>
          <Button onClick={handleUpdateTask} variant="contained">保存</Button>
        </DialogActions>
      </Dialog>

      {/* 删除任务确认对话框 */}
      <Dialog open={deleteTaskDialogOpen} onClose={() => setDeleteTaskDialogOpen(false)}>
        <DialogTitle>确认删除</DialogTitle>
        <DialogContent>
          <Typography>确定要删除任务 "{deletingTask?.task_name}" 吗？此操作不可撤销。</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTaskDialogOpen(false)}>取消</Button>
          <Button onClick={handleDeleteTask} color="error" variant="contained">删除</Button>
        </DialogActions>
      </Dialog>

      {/* 导入对话框 */}
      <DepartmentSelfEvaluationIndicatorImportDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        projectId={projectId || ''}
        onImportSuccess={handleImportSuccess}
      />

      {/* 文档管理对话框 */}
      <UploadedDocumentsDialog
        open={documentsDialogOpen}
        onClose={() => setDocumentsDialogOpen(false)}
        projectId={projectId?.toString() || ''}
        onUploadComplete={() => {
          fetchIndicators();
        }}
      />
    </Box>
  );
};

export default DepartmentSelfEvaluationIndicatorManager;
